import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "wouter";
import usePermissions from "@/hooks/usePermissions";
import { isBackendPending } from "@/config/backendPending";
import { ALL_NAV_ITEMS, getActiveNavItemId, matchedNavPath, type NavItem } from "@/components/layout/navConfig";

/** One breadcrumb. The last crumb of a trail is always rendered as plain text. */
export interface Crumb {
  label: string;
  href?: string;
}

/**
 * `App.tsx` route patterns (wouter syntax). Used to tell a known route that has no
 * visible menu item (e.g. `/auth/callback`, a hidden pending page) — no trail — from a
 * truly unknown path — "Not found" — and to decide which intermediate crumbs can link.
 * Must list every route no menu item covers (guarded by topbar.test.tsx); routes under
 * a menu item's path may be added here too (one line per route, keep-both on conflict).
 */
export const APP_ROUTE_PATTERNS: readonly string[] = [
  "/",
  "/dashboard",
  "/auth/callback",
  "/organizations",
  "/organizations/:organizationId",
  "/bookings/:bookingId/marketplace/services/:serviceId",
  "/bookings/:bookingId/marketplace",
  "/bookings/:bookingId",
  "/bookings",
  "/vendors",
  "/perks/create",
  "/perks/:perkId/edit",
  "/perks/:perkId",
  "/perks",
  "/perk-categories",
  "/perk-locations",
  "/feed-stickers",
  "/kudos-types",
  "/activations/templates/new",
  "/activations/templates/:templateId",
  "/activations/templates",
  "/activations/events/:eventId/results",
  "/activations/events/:eventId/operator",
  "/activations/events/:eventId/leads",
  "/activations/events/:eventId",
  "/activations/events",
  "/marketplace",
  "/tenant-customizations",
  "/localization",
  "/financial-operations",
  "/billing",
  "/analytics",
  "/system-administration",
  "/service-categories",
  "/design-system",
  "/account-deletions",
];

/** Labels for static path segments below a menu item; any other segment is an id ⇒ "Details". */
const SEGMENT_LABELS: Record<string, string> = {
  new: "New",
  create: "New",
  edit: "Edit",
  results: "Results",
  operator: "Operator console",
  leads: "Leads",
  marketplace: "Marketplace",
  services: "Service",
};

const segmentsOf = (path: string) => path.split("/").filter(Boolean);

/** Whether `path` matches a wouter route pattern (`:param` = any one segment). */
export function matchesRoutePattern(pattern: string, path: string): boolean {
  const want = segmentsOf(pattern);
  const have = segmentsOf(path);
  return want.length === have.length && want.every((seg, i) => seg.startsWith(":") || seg === have[i]);
}

function isKnownRoute(path: string): boolean {
  return APP_ROUTE_PATTERNS.some((pattern) => matchesRoutePattern(pattern, path));
}

/**
 * The trail for `location` when the page names none: the active menu item (linked),
 * then one crumb per extra segment. `null` = show no trail.
 */
export function buildDefaultTrail(location: string, visibleItems: NavItem[]): Crumb[] | null {
  const path = location.replace(/\/+$/, "") || "/";
  const activeId = getActiveNavItemId(path, visibleItems);
  const item = visibleItems.find((candidate) => candidate.id === activeId);
  if (!item) return isKnownRoute(path) ? null : [{ label: "Not found" }];

  const base = matchedNavPath(item, path) ?? item.path;
  const crumbs: Crumb[] = [{ label: item.label, href: item.path }];
  let current = base;
  for (const segment of segmentsOf(path.slice(base.length))) {
    current += `/${segment}`;
    crumbs.push({ label: SEGMENT_LABELS[segment] ?? "Details", href: isKnownRoute(current) ? current : undefined });
  }
  return crumbs;
}

interface BreadcrumbContextValue {
  pageCrumbs: Crumb[] | null;
  setPageCrumbs: (crumbs: Crumb[] | null) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | undefined>(undefined);

function useBreadcrumbContext(): BreadcrumbContextValue {
  const context = useContext(BreadcrumbContext);
  if (!context) throw new Error("Breadcrumb hooks must be used inside a BreadcrumbProvider");
  return context;
}

interface BreadcrumbProviderProps {
  children: ReactNode;
}

/** Holds the crumbs a page declared via `usePageCrumbs` (mounted once in `Shell`). */
export function BreadcrumbProvider({ children }: BreadcrumbProviderProps) {
  const [pageCrumbs, setPageCrumbs] = useState<Crumb[] | null>(null);
  const value = useMemo(() => ({ pageCrumbs, setPageCrumbs }), [pageCrumbs]);
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

/**
 * Lets a page name its deeper levels, e.g.
 * `usePageCrumbs([{ label: "Help Center", href: "/help-center" }, { label: "Articles", href: … }, { label: title }])`.
 * `null` keeps the default trail. Cleared when the page unmounts.
 */
export function usePageCrumbs(crumbs: Crumb[] | null): void {
  const { setPageCrumbs } = useBreadcrumbContext();
  const key = crumbs ? JSON.stringify(crumbs) : null;
  useEffect(() => {
    setPageCrumbs(key ? (JSON.parse(key) as Crumb[]) : null);
    return () => setPageCrumbs(null);
  }, [key, setPageCrumbs]);
}

/** The trail the top bar renders: the page's own crumbs, else the default trail. */
export function useBreadcrumbTrail(): Crumb[] | null {
  const { pageCrumbs } = useBreadcrumbContext();
  const [location] = useLocation();
  const { canAccessMenu } = usePermissions();
  if (pageCrumbs && pageCrumbs.length > 0) return pageCrumbs;
  const visibleItems = ALL_NAV_ITEMS.filter((item) => canAccessMenu(item.id) && !isBackendPending(item.id));
  return buildDefaultTrail(location, visibleItems);
}
